import type { NoteName, OctaveNumber } from 'shared/definitions/musicTheory/notesAndAccidentals';
import {
  getPianoScreenReaderNamesFromMidi,
  midiFlats,
  midiNumbersBasic,
  midiSharps,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isNumeric } from 'shared/src/utils/isNumeric';
import Note from './note';
import type KeySignature from './keySignature';

declare type MidiLike = Note | NoteName | number;

export function toMidi(noteNoteNameOrMidiNumber: MidiLike): number {
  if (isNumeric(noteNoteNameOrMidiNumber)) {
    const midiNumber = Number(noteNoteNameOrMidiNumber);
    if (midiNumber % 1 !== 0) {
      throw Error(`midi must be an integer, not ${midiNumber}`);
    }
    return midiNumber;
  }
  if (noteNoteNameOrMidiNumber instanceof Note) {
    return noteNoteNameOrMidiNumber.getMidiNumber();
  }
  if (typeof noteNoteNameOrMidiNumber === 'string') {
    const note = new Note(noteNoteNameOrMidiNumber);
    return note.getMidiNumber();
  }
  throw Error('toMidi takes only a Note, string or number');
}

export function toLocationString(noteNoteNameOrMidiNumber: MidiLike): string {
  const midi = toMidi(noteNoteNameOrMidiNumber);
  return getPianoScreenReaderNamesFromMidi(midi);
}

/**
 * Return screen-reader accessible description of a midi/note.
 * ```typescript
 *  toNoteNamesString('C#'); // 'C-sharp/D-flat'
 *  toNoteNamesString('F'); // 'F'
 * ```
 */
export function toNoteNamesString(
  noteNoteNameOrMidiNumber: MidiLike,
  includeOctave = false
): string {
  const midi = toMidi(noteNoteNameOrMidiNumber);
  const isWhite = [0, 2, 4, 5, 7, 9, 11].includes(midi % 12);
  const noteNames = isWhite
    ? [midiNumbersBasic[(midi % 12) as keyof typeof midiNumbersBasic]]
    : [midiSharps[midi % 12]!, midiFlats[midi % 12]!];
  const octave = ~~(midi / 12);

  if (!noteNames.every((note) => !!note))
    throw Error(
      `Midi must be a positive integer to get note name string, not ${noteNoteNameOrMidiNumber} ==> ${midi}`
    );

  return noteNames
    .map((name) => new Note((name + octave) as NoteName))
    .map((note, index) =>
      note.toAriaString({ octave: includeOctave && (noteNames.length === 1 || index > 0) })
    )
    .join('/');
}

export function toNameInKey(
  noteNoteNameOrMidiNumber: MidiLike,
  key: KeySignature,
  includeOctave = false
): string {
  const midi = toMidi(noteNoteNameOrMidiNumber);
  const scale = key.getScale();
  const notes: Note[] = Object.values(scale);
  const note = notes.find((n) => n.getMidiNumber() % 12 === midi % 12);
  if (note && includeOctave) {
    let loopCount = 0;
    const direction = note.getMidiNumber() < midi ? 1 : -1;
    while (note.getMidiNumber() !== midi && ++loopCount < 15) {
      note.octave += direction;
    }
    return note.toString();
  }
  if (note) return note.toAriaString({ octave: includeOctave });
  return key.accidentalsCount < 0
    ? toFlatName(midi, includeOctave)
    : key.accidentalsCount > 0
    ? toSharpName(midi, includeOctave)
    : toNoteNamesString(midi, includeOctave);
}

export function toSharpName(noteNoteNameOrMidiNumber: MidiLike, includeOctave = false) {
  const midi = toMidi(noteNoteNameOrMidiNumber);
  const noteName = midiSharps[midi % 12];
  const octave = includeOctave ? ~~(midi / 12) : '';
  const note = new Note(`${noteName}${octave as OctaveNumber | ''}` as NoteName);
  return note.toAriaString({ octave: includeOctave });
}

export function toFlatName(noteNoteNameOrMidiNumber: MidiLike, includeOctave = false) {
  const midi = toMidi(noteNoteNameOrMidiNumber);
  const noteName = midiFlats[midi % 12];
  const octave = includeOctave ? ~~(midi / 12) : '';
  const note = new Note(`${noteName}${octave as OctaveNumber | ''}` as NoteName);
  return note.toAriaString({ octave: includeOctave });
}
